/**
 * CMS API client — client-safe surface
 *
 * Fetches data from the standalone Payload CMS.
 * Development: http://localhost:3002/api
 * Production: set NEXT_PUBLIC_CMS_API_URL env var
 *
 * This module is safe to import from client components. It contains:
 *  - All shared types
 *  - URL/locale helpers (`cmsMediaUrl`, `cmsLocale`, `jobSlug`)
 *  - The plain `cmsGet` fetcher (no Next.js cache directives)
 *  - Public accessors that work in both server and client contexts
 *
 * Server-side cached wrappers live in `lib/cms-server.ts`. Pages and other
 * server-only code should import the cached wrappers from there so SSG
 * benefits from `'use cache'` + `cacheLife` + `cacheTag`. Client components
 * (e.g. `useEffect` callers) import the plain accessors from this file.
 *
 * All functions return empty arrays / null on failure (graceful fallback to
 * hardcoded data). Errors are reported to Sentry via `next/server`'s `after()`
 * to avoid `Date.now()` prerender bailouts under Cache Components.
 */

/** Skip Sentry reporting for headless browsers / bots (noisy, non-actionable). */
function isBot(): boolean {
  if (typeof navigator === "undefined") return false
  return /HeadlessChrome|Lighthouse|Googlebot|bingbot|Bytespider/i.test(
    navigator.userAgent
  )
}

/** Transient network errors (DNS, connection refused, TLS) — report as warning, not error. */
function isTransientNetworkError(err: unknown): boolean {
  if (!(err instanceof TypeError)) return false
  const msg = err.message.toLowerCase()
  return (
    msg.includes("failed to fetch") ||
    msg.includes("fetch failed") ||
    msg.includes("network request failed")
  )
}

/**
 * Defer Sentry reporting to after the response lifecycle via next/server `after()`.
 * Sentry's captureMessage/captureException call Date.now() internally to timestamp
 * events, which triggers NEXT_STATIC_GEN_BAILOUT during prerendering. Using after()
 * ensures the Date.now() call happens outside the prerender context.
 */
async function reportToSentry(
  level: "warning" | "error",
  messageOrError: string | Error,
  extra: Record<string, unknown>
) {
  const send = async () => {
    try {
      const Sentry = await import("@sentry/nextjs")
      if (typeof messageOrError === "string") {
        Sentry.captureMessage(messageOrError, { level, extra })
      } else {
        Sentry.captureException(messageOrError, { extra })
      }
    } catch {
      // Sentry unavailable — swallow silently
    }
  }

  // Prefer after() to defer reporting outside the response stream,
  // but fall back to direct send when after() isn't available
  // (e.g. inside "use cache" during prerender where there's no request context).
  try {
    const { after } = await import("next/server")
    after(send)
  } catch {
    await send()
  }
}

const DEFAULT_CMS_API_URL = "https://cms.aheadhealth.dev/api"

const CMS_API_URL = process.env.NEXT_PUBLIC_CMS_API_URL || DEFAULT_CMS_API_URL
export const CMS_BASE_URL = (
  process.env.NEXT_PUBLIC_CMS_API_URL || DEFAULT_CMS_API_URL
)
  .trim()
  .replace(/\/api\/?$/, "")

export function cmsMediaUrl(url: string | undefined | null): string {
  if (!url) return ""
  if (url.startsWith("http")) return url
  if (!CMS_BASE_URL) return ""
  return CMS_BASE_URL + url
}

/**
 * Map (URL locale, country) → CMS locale, read off the message-file registry.
 * NL English, DE German, and AT German each get their own BCP47 CMS locale
 * (`en-NL`, `de-DE`, `de-AT`) so editors can write jurisdiction-specific copy
 * (NL legal language, HWG-compliant German claims, Austrian
 * Sozialversicherung framing) without bleeding into the Swiss-context
 * defaults. Throws on a pair with no catalog — same contract as
 * `getMessageFile`.
 */
export function cmsLocale(locale: string, country: string): string {
  return MESSAGE_FILES[getMessageFile(locale, country)].cmsLocale
}

/** Build the `&locale=…` query-string fragment (empty for English / missing). */
export function localeParam(locale?: string): string {
  return locale && locale !== "en" ? `&locale=${locale}` : ""
}

/**
 * Pre-launch CMS locales: a Payload fetch with `?locale=de-DE` falls back to
 * `defaultLocale=en` when no German-Germany translation exists, which leaks
 * Swiss-context content (CH legal claims, IRM terminology) onto the German
 * surface — wrong for HWG/Telemedizin compliance and confusing for users.
 *
 * Until each (collection, document) has a real `de-DE` / `en-NL` / `de-AT` /
 * `en-DE` value, callers should suppress Payload's automatic fallback for these
 * locales so a missing translation surfaces as "empty" instead of "Swiss
 * content". CH locales (en/de/fr) keep the default fallback behavior — a
 * missing French translation on CH-FR should still fall back to the
 * configured base.
 */
const FALLBACK_SUPPRESSED_CMS_LOCALES = new Set([
  "de-DE",
  "en-NL",
  "de-AT",
  "en-DE",
])

export function shouldSuppressCmsFallback(locale?: string): boolean {
  return !!locale && FALLBACK_SUPPRESSED_CMS_LOCALES.has(locale)
}

/**
 * A suppressed locale may BORROW a sibling locale's copy instead of coming
 * back empty — but only where borrowing is demonstrably safe.
 *
 * `de-AT` borrows `de-DE`. The suppression above exists to stop Payload
 * falling back to `defaultLocale=en`, which would put Swiss/English copy on an
 * Austrian surface. That reasoning never applied to German-from-Germany: it is
 * the same language, and written formal Austrian German is close enough that
 * `docs/austrian-german-terminology.md` concludes most copy needs no lexical
 * change at all.
 *
 * `en-NL` borrows `en` for the same reason (AHD-12964). NL-English is the
 * same language as the Swiss-English source, and the suppression was only ever
 * aimed at market-specific copy — but it also emptied the one collection that
 * carries none, so every one of the 511 diseases rendered on
 * `/nl/en/what-we-detect` with a blank description (en-NL is 0% translated).
 *
 * ONLY FOR CLINICAL COLLECTIONS. Checked before shipping: 0 of 200 sampled
 * `de-DE` disease descriptions contain a Germany-specific term (GKV, IGeL,
 * Krankenkasse, Deutschland), and 0 of all 511 `en` ones contain a Swiss one
 * (Switzerland, Swiss, CHF, canton, insurance) — they are plain medical
 * descriptions. That is emphatically NOT true of FAQ, legal or insurance copy,
 * where de-DE talks about statutory health insurance concepts Austria does not
 * have and `en` quotes CH prices and KVG terms. Do not widen this to those
 * collections; the market layer in `docs/austrian-german-terminology.md` calls
 * that out as the single biggest de-AT positioning trap.
 */
const CMS_LOCALE_BORROWS_FROM: Record<string, string> = {
  "de-AT": "de-DE",
  "en-NL": "en",
  // `/de/en`: the same plain-English clinical copy as en-NL. Germany's market
  // specifics live in the non-clinical collections, which stay suppressed.
  "en-DE": "en",
}

/** The locale `locale` borrows clinical copy from, if any. */
export function cmsBorrowLocale(locale?: string): string | undefined {
  return locale ? CMS_LOCALE_BORROWS_FROM[locale] : undefined
}

/**
 * Payload REST API: passing `?fallback-locale=null` (literal string "null")
 * disables the per-request fallback chain. Use alongside `localeParam(locale)`
 * — empty string when fallback should stay on.
 */
export function fallbackLocaleSuppressParam(locale?: string): string {
  return shouldSuppressCmsFallback(locale) ? "&fallback-locale=null" : ""
}

function previewLocaleParam(locale?: string): string {
  return locale ? `&locale=${locale}` : ""
}

function previewFallbackSuppressParam(locale?: string): string {
  return locale ? "&fallback-locale=null" : ""
}

// ─── Types ───────────────────────────────────────────────────

export interface CmsClinicHours {
  day: string
  time: string
  id?: string
}

/**
 * Pointer into the `products` collection (the PIM). Populated when locations
 * are fetched with `?depth>=1`; `name` arrives already resolved for the
 * requested locale because the locations fetch carries `locale=` +
 * `fallback-locale=en`.
 *
 * Only the identity + routing fields the clinic pages render are declared. The
 * document Payload actually sends is far bigger — copy fields, `prices`, and
 * at `depth=2` the expanded `markers[].biomarker` / `addOnTo` / `excludes`
 * relations. Never read those here: money is market-scoped and comes from
 * `usePrices()` / `buildFallback(country)` (see `/market-divergence`), and the
 * rest is payload this surface has no use for.
 */
export interface CmsProductRef {
  id: number | string
  slug: string
  name: string
  category: "imaging" | "blood"
  path?: string | null
}

/** Per-country availability status for a service in the carousel. */
export type ServiceMarketStatus =
  | "available"
  | "coming_soon"
  | "waitlist"
  | "hidden"
  /**
   * Service exists in this market but is not bookable yet — editors toggle
   * this per country (marketing-site-cms #214). Drives the terracotta "Not
   * available" pill on imaging add-ons + the greyed-out booking CTA notice on
   * test detail pages (`isServiceNotAvailable` in `lib/services-overlay.ts`).
   * Distinct from `hidden` (excluded entirely) and `coming_soon`/`waitlist`
   * (which route the CTA to the waitlist modal).
   */
  | "not_available"

/**
 * One per-country availability / ordering override row on a control-plane doc
 * (`products` since AHD-12802 phase 1; `services` mirrors it until phase 2).
 * Omitting a country inherits the global default (available, global `order`).
 */
export interface CmsServiceMarketEntry {
  /** Lowercase 2-letter country code (ch, nl, de, at). */
  country: string
  status: ServiceMarketStatus
  /** Per-country sort override; falls back to the global `order` when null. */
  order?: number | null
  /**
   * Hide this service from the CORE NAVIGATION (desktop mega menu + mobile
   * slide-out menu) for this country. Orthogonal to `status`: a `waitlist` /
   * `coming_soon` service can still carry its carousel pill while being pulled
   * out of the menu. Defaults to false (shown) when absent. Does NOT affect the
   * services carousel, the footer, or the direct `/tests/<slug>` page.
   */
  hideFromNav?: boolean | null
}

/**
 * Control-plane projection of a `products` row: the per-country gating,
 * coming-soon, and sort-order fields the carousel overlays onto the static
 * `data/tests.ts` content, joined by `slug`. Locale-agnostic — these fields
 * are not localized in the CMS, so the loader fetches them once.
 *
 * Disjoint from `CmsProductRef` (Locations.imagingServices / bloodServices),
 * which projects the identity + routing half of the same collection: the
 * control plane never reads `name`, and the clinic rows never read the
 * gating fields.
 */
export interface CmsServiceConfig {
  id?: number | string
  slug: string
  category: "imaging" | "blood"
  path?: string | null
  /** Global default sort order; lower = earlier. Defaults to 0 in the CMS. */
  order: number
  /** Supabase products table id the booking app resolves to a Stripe price. */
  bookingProductId?: string | null
  marketStatus?: CmsServiceMarketEntry[] | null
}

/**
 * One `locations.imagingServices[]` / `bloodServices[]` row. The deprecated
 * `service` relation is deliberately NOT declared here (AHD-12802 phase 1):
 * the CMS keeps the column until phase 2, but nothing on the marketing site
 * may read it, so a row whose `product` is unset must surface as a skipped
 * row rather than fall back and hide the data problem.
 */
export interface CmsClinicService {
  id?: string
  addon?: boolean
  // CMS field is optional — runtime-guarded in renderCmsService.
  product?: CmsProductRef | null
}

/**
 * Render-ready shape returned by `resolveImagingServices` /
 * `resolveBloodServices`. `name` + `path` are projected from the related
 * product entry; there is no static fallback (the old `data/clinics.ts` one
 * is long gone), so a row the CMS cannot resolve is dropped, not defaulted.
 */
export interface ResolvedClinicService {
  name: string
  path: string | null
  addon: boolean
}

export interface CmsClinicParkingSection {
  heading?: string | null
  body?: string | null
  bullets?: { value: string; id?: string }[] | null
  id?: string
}

export interface CmsLocationImage {
  id?: number | string
  url?: string
  alt?: string
}

/**
 * `CmsCity` is the parent entity that owns city-level identity and content.
 * One city has 0..N clinics (active partners) and may also be in waitlist
 * mode (status === "coming-soon", zero active clinics).
 *
 * Phase 1 of the cities/clinics split keeps the legacy denormalized fields
 * on `CmsLocation` (slug, country, status, posterImage, displayName,
 * displayOrder) populated alongside the new `cityRef` relationship, so old
 * marketing-site code keeps rendering. Phase 2 removes those denormalized
 * fields once every consumer has migrated to reading `cityRef` directly.
 */
export interface CmsCity {
  id: number
  /** Canonical English city name (admin list title). */
  city: string
  /** URL-safe city slug used in /locations/{slug}. Unique. */
  slug: string
  /** Localized display name (Geneva → Genf, Vienna → Wien…). */
  displayName?: string | null
  /** ISO country code (CH, NL, AT, DE, GB, BE, SE, FR, DK). */
  country: string
  /** Whether the city has any active partner clinics. */
  status: "active" | "coming-soon"
  displayOrder?: number | null
  heroImage?: CmsLocationImage | null
  /** Used by the NL /locations poster grid for coming-soon cities. */
  posterImage?: CmsLocationImage | null
  /** Future-use city-level marketing copy. Currently unused by the site. */
  description?: string | null
  updatedAt?: string
}

/** A `countries` collection row from the CMS (business hours + holidays). */
export interface CmsCountry {
  id: number
  country: string
  timezone: string
  weekdays: string[]
  startHour: number
  endHour: number
  halfDayEndHour: number
  holidays?:
    | {
        date: string
        label: string
        observance: "full-closure" | "half-day"
      }[]
    | null
}

/**
 * `CmsLocation` is a partner clinic in the CMS. The city / country / waitlist
 * status now live on the parent `CmsCity` row, reachable via `cityRef`. Always
 * fetch with `?depth=1+` and `select[cityRef]=true` so `cityRef` arrives
 * populated as a full `CmsCity`.
 *
 * Per-locale strings (`description`, `languageNote`, `publicTransport`,
 * `hours[].time` / `hours[].day`, `parking[]` text, service names) are
 * localized via Payload's per-locale storage — the marketing site fetches
 * with `?locale=…&fallback-locale=en`.
 */
export interface CmsLocation {
  id: string
  /**
   * Legacy city-level fields kept temporarily during the phase 2/3 migration.
   * Runtime code still hydrates them from `cityRef` so older consumers keep
   * rendering until every call site reads via `cityOf(loc)` directly.
   */
  city?: string | null
  slug?: string | null
  country?: string | null
  status?: "active" | "coming-soon" | null
  displayName?: string | null
  posterImage?: CmsLocationImage | null
  /**
   * Clinic-level sort key. Lower = first within a city. Defaults to 99 in
   * the CMS so unsorted clinics fall back to the type-tiebreaker (MRI first).
   */
  displayOrder?: number | null
  clinic?: string | null
  /** URL-safe clinic slug for `/locations/[slug]/[clinicSlug]`. */
  clinicSlug?: string | null
  address?: string | null
  type?: "mri" | "blood" | null
  lat?: number | null
  lng?: number | null
  /**
   * Google place id of the Business Profile Ahead owns for this clinic. Empty
   * for a clinic inside partner premises — their listing is not ours to
   * claim as `sameAs`.
   */
  placeId?: string | null
  /** Square thumbnail used on the locations index card. */
  cardImage?: CmsLocationImage | null
  /** Large hero image used on the city / clinic detail pages. */
  heroImage?: CmsLocationImage | null
  description?: string | null
  languageNote?: string | null
  publicTransport?: string | null
  hours?: CmsClinicHours[] | null
  parking?: CmsClinicParkingSection[] | null
  imagingServices?: CmsClinicService[] | null
  bloodServices?: CmsClinicService[] | null
  /**
   * Parent city. Always populated as a full `CmsCity` on this site (we fetch
   * with `?depth=1` and `select[cityRef]=true`). The numeric form is the
   * Payload wire shape when an un-populated id arrives — `cityOf()` throws
   * on that case so a misconfigured fetch fails loudly.
   */
  cityRef: CmsCity | number
  updatedAt?: string
}

/**
 * Server-only header that lets preview fetches read drafts from the CMS.
 *
 * The CMS gates anonymous reads with `_status=published` so unpublished
 * drafts can't leak through the public REST API. The marketing-site preview
 * route also runs server-side and needs draft visibility — it presents the
 * shared `PAYLOAD_PREVIEW_SECRET` (the same value that already gates
 * `/api/payload-preview` here) as `X-Preview-Secret` and the CMS allows the
 * request through. The env var is server-only (no `NEXT_PUBLIC_` prefix), so
 * this resolves to `undefined` in client bundles and the helper returns no
 * header — keeping the secret out of any client-side fetch.
 */
function previewAuthHeaders(): HeadersInit {
  const secret = process.env.PAYLOAD_PREVIEW_SECRET
  return secret ? { "X-Preview-Secret": secret } : {}
}

async function cmsPreviewGet<T>(path: string, tag: string): Promise<T[]> {
  const result = await cmsGetWithStatus<T>(path, tag, {
    cache: "no-store",
    headers: previewAuthHeaders(),
  })
  return result.docs
}

export interface JobHighlight {
  title: string
  body: string
  id?: string
}

export interface JobListing {
  id: string
  title: string
  slug?: string
  department: string
  location: string
  type: string
  summary?: LexicalContent | null
  description?: LexicalContent | null
  highlights?: JobHighlight[]
  applicationUrl?: string
  order: number
  _status?: "draft" | "published"
}

/** Derive a URL-safe slug from a job title. */
export function jobSlug(job: JobListing): string {
  if (job.slug) return job.slug
  // Preview drafts can lack a title (untitled work-in-progress); fall back to
  // the id so the slug stays unique and non-empty instead of crashing.
  if (!job.title) return job.id
  return job.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

export interface CmsFaqCategory {
  id: number | string
  slug: string
  name: string
  description?: string
  order: number
}

export interface FaqItem {
  id: string
  slug: string
  question: string
  answer: string
  category: string
  pages?: string[]
  featured?: boolean
  order: number
  updatedAt?: string
  hideFromFaqIndex?: boolean
  /**
   * Markets where the editor has hidden this FAQ (CMS `hideInCountries`).
   * Empty / absent = visible everywhere. Filtered via `isHiddenInCountry`.
   */
  hideInCountries?: Country[]
  /**
   * Auto-populated by Payload's `join` field on the `faq` collection — the
   * blog posts that point at this FAQ via `blog_posts.relatedFaqs`. Only the
   * fields needed for the backlink card are read; widen as needed.
   */
  relatedBlogPosts?: {
    docs?: Array<{
      id: number
      slug: string
      title: string
      publishedDate?: string
      category?: string
    }>
  } | null
}

/**
 * Listing projection of {@link FaqItem}, i.e. what {@link FAQ_LIST_SELECT}
 * actually returns on the browser path.
 *
 * Unlike {@link CmsSymptomListItem} this `Omit` is a real structural guard,
 * not documentation: `order` is declared required-non-optional above, so the
 * compiler catches a list row handed to a consumer that expects a whole
 * document. Nothing reads `.order` anywhere in `app/`, `components/`, `lib/`
 * or `scripts/`: the ordering is `sort=order`, executed by Payload.
 *
 * `relatedBlogPosts` is dropped for a different reason: it is a Payload JOIN
 * field, and the browser URL asks for no `joins[relatedBlogPosts]` clause, so
 * it never arrives. The server twin (`faqIndex` in `lib/cms-server.ts`) does
 * ask for it and keeps the full `FaqItem` for that reason.
 */
export type FaqListItem = Omit<FaqItem, "order" | "relatedBlogPosts">

// ─── Fetch helpers ───────────────────────────────────────────

/**
 * Client-side CMS fetch with Sentry error logging.
 * Kept for backwards compatibility with client components that already use it.
 */
export async function cmsFetchClient<T>(
  path: string,
  tag: string
): Promise<T[]> {
  return cmsGet<T>(path, tag)
}

/**
 * Plain CMS fetcher with Sentry error logging — works in both server and
 * client contexts. The underlying fetch tags each request with the
 * collection name and a `CMS_CACHE_REVALIDATE_SECONDS` revalidate ceiling, so
 * `revalidateTag(...)` from `app/api/revalidate/route.ts` (the CMS webhook)
 * actually busts the cache — and any locale whose webhook misfires is still
 * eventually consistent within that window.
 *
 * Exported so `lib/cms-server.ts` can build cached wrappers around it.
 */
export async function cmsGet<T>(path: string, tag: string): Promise<T[]> {
  const result = await cmsGetWithStatus<T>(path, tag)
  return result.docs
}

/**
 * In-flight request coalescing, browser only.
 *
 * The only caching on `cmsGetWithStatus` is `next: { revalidate, tags }`,
 * which is Next's *server* fetch cache — in the browser it's an inert option
 * on a plain `fetch()`. So two components mounting in the same tick and asking
 * for the same collection each download it in full.
 *
 * That is not hypothetical. On `/ch/de/go/checkup`, `BiomarkerDiseaseExplorer`
 * drove both `getCMSBiomarkers` and `getCMSBiomarkersByCategory` (and the
 * disease equivalents), whose URLs are byte-identical. Measured on production
 * 2026-07-28 — each pair fired ~160 ms apart and both downloaded:
 *
 *   510 KB  biomarkers?…limit=300   start 632 ms   end 1932 ms
 *   567 KB  diseases?…limit=500     start 634 ms   end 2023 ms
 *   511 KB  biomarkers?…limit=300   start 796 ms   end 3189 ms   <- duplicate
 *   567 KB  diseases?…limit=500     start 796 ms   end 3592 ms   <- duplicate
 *
 * 1.07 MB of pure waste, landing in the middle of the LCP window (LCP 5153 ms).
 * `getCMSBiomarkersByCategory`'s own docblock already claims it "shares the
 * cached full-list response … instead of issuing a second query" — true on the
 * server, false in the browser. This makes it true in both.
 *
 * The explorer itself no longer calls the `*ByCategory` helpers (it imports
 * `getCMSBiomarkers` / `getCMSDiseases` / `getCMSCategories` /
 * `getCMSSymptoms` / `getCMSSymptomGroups`), so nothing exercises that pair
 * today. The invariant still binds every helper meant to share a response:
 * the URLs must stay byte-identical, projection included, or the key stops
 * matching and the coalescing silently does nothing (AHD-9301).
 *
 * Scope, deliberately narrow:
 *  - **Browser only.** On the server Next's fetch cache and the `"use cache"`
 *    wrappers in `cms-server.ts` already dedupe, and a module-level map would
 *    leak across requests in a long-lived server process.
 *  - **In-flight only.** The entry is dropped as soon as the promise settles,
 *    so this coalesces concurrent callers and never becomes a response cache
 *    with its own staleness rules. A later mount re-fetches and gets the
 *    browser's HTTP cache, which is the correct owner of that decision.
 *  - **Failures are shared too**, then dropped — a rejected or `ok: false`
 *    result is not retained, so the next caller retries normally. The retry
 *    loop inside `cmsGetWithStatus` is unaffected: it runs once for the shared
 *    request rather than once per duplicate caller.
 */
const inFlightCmsGets = new Map<
  string,
  Promise<{ docs: unknown[]; ok: boolean }>
>()

export async function cmsGetDeduped<T>(
  path: string,
  tag: string
): Promise<{ docs: T[]; ok: boolean }> {
  if (typeof window === "undefined") {
    return cmsGetWithStatus<T>(path, tag)
  }
  const key = `${tag}::${path}`
  const existing = inFlightCmsGets.get(key)
  if (existing) return existing as Promise<{ docs: T[]; ok: boolean }>

  const pending = cmsGetWithStatus<T>(path, tag).finally(() => {
    inFlightCmsGets.delete(key)
  }) as Promise<{ docs: unknown[]; ok: boolean }>
  inFlightCmsGets.set(key, pending)
  return pending as Promise<{ docs: T[]; ok: boolean }>
}

/** Test seam — the map is module state and would otherwise leak between specs. */
export function __resetCmsGetDedupeForTests(): void {
  inFlightCmsGets.clear()
}

/** Whether the error is transient and worth retrying (timeout, network). */
function isRetryable(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "TimeoutError") return true
  if (err instanceof DOMException && err.name === "AbortError") return true
  return isTransientNetworkError(err)
}

const MAX_ATTEMPTS = 6

/**
 * Maximum wall-clock time (ms) the retry loop is allowed to run.
 *
 * This function is called inside `"use cache"` wrappers in cms-server.ts.
 * Next.js imposes a ~50 s timeout on cache fills during prerender
 * (`USE_CACHE_TIMEOUT`). If the retry loop exceeds that, Next throws
 * "Filling a cache during prerender timed out" instead of caching the
 * graceful-fallback empty result. 30 s gives enough room for 3-4 retries
 * with backoff while staying safely under the framework limit.
 */
const RETRY_DEADLINE_MS = 30_000

/**
 * Data-cache revalidate window (seconds) for every CMS-backed fetch below.
 *
 * The CMS webhook (`app/api/revalidate/route.ts` → `revalidateTag(...)`) busts
 * the relevant tag the instant content changes, so this window is NOT the
 * freshness mechanism — it's only the fallback ceiling for the rare case where
 * a webhook misfires. Because a dynamic route inherits its ISR revalidate from
 * the minimum revalidate of the fetches it renders, this value doubles as the
 * ISR-write cadence for the entire ~10k-URL SSR content surface (biomarker /
 * disease / insights / FAQ pages, kept warm by crawlers). At the previous 1h
 * value every warm URL rewrote its ISR entry ~hourly, generating ~28M ISR
 * writes/month (~$125) with no freshness benefit the webhook wasn't already
 * providing. 24h cuts that ~24× while leaving on-demand invalidation untouched
 * — a missed webhook now self-heals within a day instead of an hour.
 */
const CMS_CACHE_REVALIDATE_SECONDS = 86_400

/**
 * Should a short page from this fetch be treated as a degraded response rather
 * than as the truth? Derived from the same registry the build-time guard uses
 * (`HARD_LIST_LIMITS`), so the two halves of the contract cannot drift.
 *
 * The registry's own invariant is "live `totalDocs` <= the entry, therefore no
 * exhaustive fetch anywhere truncates" — which is exactly the precondition
 * `expectCompletePage` documents ("only pass this for fetches whose `limit` is
 * large enough to return the full set in one page"). So any list fetch written
 * at or above its collection's registered limit is asking for the whole
 * collection, and a 200 carrying fewer rows than its own `totalDocs` is a
 * degraded Payload response (cold start, partial DB read), never a smaller
 * collection. A `limit=1` lookup, a capped top-N section, or a collection with
 * no registry entry is left alone.
 *
 * Deriving it beats opting in per call site, which is what let AHD-12498
 * happen: `categories` was the ONLY runtime fetch that ever passed the flag,
 * so every other index silently cached partials. On marketing-site that
 * surfaced as `/ch/de/careers/country-lead-germany` returning 404 while the
 * careers index still linked to it — a job-listings page that came back 2-of-3
 * with HTTP 200, cached as truth by `jobsIndex`, turned into a `notFound()`
 * and then FROZEN by Next's full route cache for the life of the deployment.
 * Nothing self-heals from that: the CMS was healthy again seconds later and
 * the page stayed 404 until the next deploy.
 */
export function expectsCompletePage(path: string): boolean {
  const collection = path.match(/^\/([a-z0-9-]+)\?/i)?.[1]
  const limit = Number(path.match(/[?&]limit=(\d+)/)?.[1])
  const registered = collection ? HARD_LIST_LIMITS[collection] : undefined
  return registered != null && Number.isFinite(limit) && limit >= registered
}

/**
 * CMS fetcher that returns both the data and whether the fetch succeeded.
 *
 * Retries up to 5 times (6 total attempts) with exponential backoff on
 * transient errors: 5xx status, 429 rate-limit, network timeouts, aborts,
 * and transient network errors. Prerendering this site fires ~1968 CMS
 * requests across 29 parallel workers and the CMS is flaky, so even a
 * sub-1% single-request failure rate breaks `next build` without
 * aggressive retrying. Backoff: 1.5s, 3s, 6s, 12s, 24s — but the total
 * wall-clock time is capped at RETRY_DEADLINE_MS so we never bust the
 * Next.js `"use cache"` prerender timeout.
 */
export async function cmsGetWithStatus<T>(
  path: string,
  tag: string,
  requestInit?: Omit<RequestInit, "signal">,
  opts?: {
    /**
     * Treat a 200 whose `docs` array is shorter than the response's own
     * `totalDocs` as a TRANSIENT failure (retry, then `ok:false`) rather than
     * a success. A degraded Payload response (cold start, partial DB read)
     * can return e.g. 35 of 37 rows with HTTP 200; without this the partial
     * is cached as "ok" and trips a downstream fail-loud guard with a
     * misleading "seed it in the CMS" error (AHD-5902 build flakiness). Only
     * pass this for fetches whose `limit` is large enough to return the full
     * set in one page (otherwise `docs.length < totalDocs` is expected).
     */
    expectCompletePage?: boolean
  }
): Promise<{
  docs: T[]
  ok: boolean
  /**
   * The collection's own `totalDocs` from the response envelope, when the
   * response carried one. `undefined` on transport failure. Lets callers
   * (scripts/validate-cms-non-empty.ts's limit-headroom guard) compare the
   * collection's true size against the hard `limit=N` the fetchers use,
   * without a second fetch.
   */
  totalDocs?: number
  /**
   * The underlying failure when `ok` is `false` — the HTTP error, the
   * incomplete-page diagnostic, or the network error that ended the retry
   * loop. Lets wrappers chain it via `new Error(msg, { cause })` so Sentry
   * captures the root failure inline instead of pointing at a companion
   * event that `after()` may have dropped during build-time prerender.
   * `undefined` on success and on retry-deadline exhaustion (no single
   * terminal error there).
   */
  cause?: Error
}> {
  // `performance.now()` (not `Date.now()`) for the retry timing: under Cache
  // Components, Next patches `Date` so any clock read during prerender trips a
  // NEXT_STATIC_GEN_BAILOUT. Next's own guidance is to use `performance` for
  // introspection (elapsed time) and reserve `Date` for output; these values
  // never reach rendered output, they only bound the retry loop.
  const startedAt = performance.now()
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const elapsed = performance.now() - startedAt
    if (attempt > 0 && elapsed >= RETRY_DEADLINE_MS) break
    const isLastAttempt = attempt === MAX_ATTEMPTS - 1
    const backoffMs = 1500 * 2 ** attempt
    // Shorten the per-request timeout when close to the deadline so the
    // fetch itself doesn't push us over.
    const remaining = RETRY_DEADLINE_MS - elapsed
    // `remaining` is a `performance.now()` float, so round to an integer —
    // `AbortSignal.timeout()` rejects non-integer delays with a RangeError.
    const fetchTimeout = Math.round(
      Math.min(10_000, Math.max(remaining - 500, 2_000))
    )
    try {
      const res = await fetch(`${CMS_API_URL}${path}`, {
        ...requestInit,
        signal: AbortSignal.timeout(fetchTimeout),
        next: { revalidate: CMS_CACHE_REVALIDATE_SECONDS, tags: [tag] },
      })
      if (!res.ok) {
        const canRetry = res.status >= 500 || res.status === 429
        if (!isLastAttempt && canRetry) {
          const timeLeft = RETRY_DEADLINE_MS - (performance.now() - startedAt)
          if (timeLeft <= 0) break
          await new Promise((r) => setTimeout(r, Math.min(backoffMs, timeLeft)))
          continue
        }
        const cause = new Error(
          `CMS HTTP ${res.status} (${res.statusText ?? ""}): ${tag}`
        )
        if (!isBot() && process.env.VERCEL_ENV === "production") {
          reportToSentry("warning", `CMS fetch failed: ${tag}`, {
            path,
            status: res.status,
            statusText: res.statusText,
          })
        }
        return { docs: [], ok: false, cause }
      }
      const data = await res.json()
      const docs: T[] = data.docs || []
      const totalDocs =
        typeof data.totalDocs === "number" ? data.totalDocs : undefined
      // Completeness guard: a 200 with fewer docs than the collection's own
      // totalDocs is a degraded/truncated response. Reclassify as transient
      // so the retry loop re-fetches the full set (build self-heals) instead
      // of caching a partial that fails loud later with a misleading message.
      if (
        (opts?.expectCompletePage ?? expectsCompletePage(path)) &&
        typeof data.totalDocs === "number" &&
        docs.length < data.totalDocs
      ) {
        if (!isLastAttempt) {
          const timeLeft = RETRY_DEADLINE_MS - (performance.now() - startedAt)
          if (timeLeft > 0) {
            await new Promise((r) =>
              setTimeout(r, Math.min(backoffMs, timeLeft))
            )
            continue
          }
        }
        const cause = new Error(
          `CMS incomplete page (${docs.length}/${data.totalDocs} docs): ${tag}`
        )
        if (!isBot() && process.env.VERCEL_ENV === "production") {
          reportToSentry("warning", `CMS fetch incomplete: ${tag}`, {
            path,
            got: docs.length,
            totalDocs: data.totalDocs,
          })
        }
        return { docs, ok: false, totalDocs, cause }
      }
      return { docs, ok: true, totalDocs }
    } catch (err) {
      if (!isLastAttempt && isRetryable(err)) {
        const timeLeft = RETRY_DEADLINE_MS - (performance.now() - startedAt)
        if (timeLeft <= 0) break
        await new Promise((r) => setTimeout(r, Math.min(backoffMs, timeLeft)))
        continue
      }
      const cause = err instanceof Error ? err : new Error(String(err))
      if (!isBot() && process.env.VERCEL_ENV === "production") {
        const level = isTransientNetworkError(err) ? "warning" : "error"
        reportToSentry(level, cause, { path, tag })
      }
      return { docs: [], ok: false, cause }
    }
  }
  return { docs: [], ok: false }
}

/**
 * Drop any location whose `cityRef` didn't populate as a full city object.
 * Every downstream consumer routes through `cityOf()`, which throws on a
 * missing city — a single un-populated row would otherwise crash every page
 * that lists or looks up locations (city overview, clinic detail, sitemap,
 * llms.txt, and the preview equivalents).
 *
 * Centralized so the public (`lib/cms.ts`), server-cached (`lib/cms-server.ts`)
 * and preview fetch paths can't drift — the preview path once missed the guard
 * the public path had, surfacing as a Sentry crash (AHD-6830).
 */
export function filterPopulatedCityRef(
  docs: CmsLocation[],
  context = "cms"
): CmsLocation[] {
  return docs.filter((loc) => {
    const ref = loc.cityRef
    const populated = !!ref && typeof ref === "object" && "slug" in ref
    if (!populated) {
      console.warn(
        `[${context}] skipping location id=${loc.id ?? "?"} clinic=${
          loc.clinic ?? "?"
        } — cityRef is not populated as an object`
      )
    }
    return populated
  })
}

// ─── Public API (uncached, client-safe) ──────────────────────
//
// These run as plain fetches everywhere. Server-side pages should import the
// cached wrappers from `lib/cms-server.ts` instead — they share the same
// names and signatures but layer `'use cache'` + `cacheLife` + `cacheTag` on
// top so SSG output stays cached and `/api/revalidate` keeps working.

export async function getLocations(
  locale?: string,
  country?: string
): Promise<CmsLocation[]> {
  // No CMS-level country filter: the `locations` collection no longer carries
  // a `country` column (phase 2 of the cities/clinics split — marketing-site-cms
  // #83 moved it to `cityRef`), so `where[country]` produces broken results.
  // Server callers in `lib/cms-server.ts` filter by `loc.country` post-hydration;
  // here we read the populated `cityRef.country` directly since this module
  // doesn't hydrate.
  const docs = filterPopulatedCityRef(
    await cmsGet<CmsLocation>(
      `/locations?where[_status][equals]=published&sort=clinic&depth=2&limit=100${localeParam(locale)}&fallback-locale=en`,
      "locations"
    )
  )
  if (!country) return docs
  const wanted = country.toUpperCase()
  return docs.filter((loc) => {
    const ref = loc.cityRef
    const refCountry =
      ref && typeof ref === "object" && "country" in ref ? ref.country : null
    return (loc.country ?? refCountry)?.toUpperCase() === wanted
  })
}

export function getCities(
  locale?: string,
  country?: string
): Promise<CmsCity[]> {
  const countryFilter = country
    ? `&where[country][equals]=${country.toUpperCase()}`
    : ""
  return cmsGet<CmsCity>(
    `/cities?where[_status][equals]=published&sort=country&depth=1&limit=100${localeParam(locale)}${countryFilter}&fallback-locale=en`,
    "cities"
  )
}

export function getJobListings(locale?: string): Promise<JobListing[]> {
  return cmsGet<JobListing>(
    `/job-listings?where[_status][equals]=published&sort=order&limit=50${localeParam(locale)}`,
    "jobs"
  )
}

/** Find a single job by slug (CMS slug field) or by matching slugified title. */
export async function getJobBySlug(
  slug: string,
  locale?: string
): Promise<JobListing | null> {
  const bySlug = await cmsGet<JobListing>(
    `/job-listings?where[slug][equals]=${encodeURIComponent(slug)}&where[_status][equals]=published&limit=1${localeParam(locale)}`,
    "jobs"
  )
  if (bySlug[0]) return bySlug[0]
  const all = await getJobListings(locale)
  return all.find((j) => jobSlug(j) === slug) || null
}

/**
 * Field projection for FAQ LIST fetches. Lives here (not in
 * `lib/cms-server.ts`, which imports it) so the browser path and the cached
 * server path cannot drift, the same arrangement as
 * {@link BIOMARKER_LIST_SELECT}.
 *
 * The browser fetch below reads 6 of these 9 fields; `pages`, `featured` and
 * `updatedAt` cost ~4.3 KB across 100 documents and are what let the server
 * index reuse the constant verbatim (it filters `pages` in memory).
 *
 * Measured against the live CMS 2026-07-29 on the browser URL below, 100 of
 * 171 published docs:
 *
 *   locale=en   2,465,049 B  ->   94,533 B   (96.2% smaller)
 *   locale=de   2,550,851 B  ->  105,621 B   (95.9% smaller)
 *   locale=fr   1,869,383 B  ->  108,676 B   (94.2% smaller)
 *
 * No `depth` pin is needed, and that is a property of THIS projection rather
 * than of the collection: ~99% of an unprojected FAQ document is the
 * `relatedBlogPosts` join expanded to whole articles, and the collection's one
 * true relationship field is `categoryV2`. The projection keeps neither, and
 * Payload never expands a field a `select` excluded, so `depth` 0, 1, 2 and
 * default all return byte-identical responses. (`category` is a plain string,
 * not the relationship its name suggests.)
 *
 * Parity was verified document by document rather than inferred: plain vs
 * projected for en/de/fr/it, identical slug sets, zero value diffs across the
 * six fields the consumer reads, identical non-null occupancy.
 *
 * The fetch below asks for `limit=500`, matching the server twin: at
 * `limit=100` against 171 published docs the static search index silently
 * omitted the tail, so 71 published FAQs were unfindable in site search
 * (AHD-9376), and at `limit=200` the 202nd published FAQ 500'd every FAQ
 * detail page at runtime (MARKETING-SITE-149, 2026-09-30). The projection
 * keeps the full set small (~0.17 MB at 171 docs vs ~4.2 MB unprojected).
 * Crossing 500 is caught loudly by the
 * `faq` completeness assert in `scripts/validate-cms-non-empty.ts`, which
 * fails the build before the index is written.
 */
export const FAQ_LIST_SELECT =
  "&select[slug]=true&select[question]=true&select[answer]=true&select[category]=true&select[hideFromFaqIndex]=true&select[hideInCountries]=true&select[pages]=true&select[featured]=true&select[updatedAt]=true"

export async function getFaqItems(
  locale?: string,
  country?: string
): Promise<FaqListItem[]> {
  const docs = await cmsGet<FaqListItem>(
    `/faq?where[_status][equals]=published&sort=order&limit=500${FAQ_LIST_SELECT}${localeParam(locale)}`,
    "faq"
  )
  if (!country) return docs
  return docs.filter((f) => !isHiddenInCountry(f, country))
}

export function getFaqItemBySlug(
  slug: string,
  locale?: string
): Promise<FaqItem | null> {
  return cmsGet<FaqItem>(
    `/faq?where[slug][equals]=${encodeURIComponent(slug)}&where[_status][equals]=published&limit=1${localeParam(locale)}`,
    "faq"
  ).then((docs) => docs[0] || null)
}

export function getFaqItemsByPage(
  page: string,
  locale?: string
): Promise<FaqListItem[]> {
  return cmsGet<FaqListItem>(
    `/faq?where[pages][contains]=${encodeURIComponent(page)}&where[_status][equals]=published&sort=order&limit=8${FAQ_LIST_SELECT}${localeParam(locale)}`,
    "faq"
  )
}

// ── Authors ──────────────────────────────────────────────────────────────

export interface CmsAuthor {
  id: number
  name: string
  slug: string
  role: string
  bio?: string
  photo?: { url: string; alt?: string } | null
  type: "author" | "reviewer"
  featuredExpert?: boolean
  credentials?: string
  linkedinUrl?: string
  companies?: { name: string; logo?: { url: string; alt?: string } | null }[]
}

export function getAuthors(locale?: string): Promise<CmsAuthor[]> {
  return cmsGet<CmsAuthor>(
    `/authors?where[_status][equals]=published&limit=50${localeParam(locale)}`,
    "authors"
  )
}

export function getAuthorBySlug(
  slug: string,
  locale?: string
): Promise<CmsAuthor | null> {
  return cmsGet<CmsAuthor>(
    `/authors?where[slug][equals]=${encodeURIComponent(slug)}&where[_status][equals]=published&limit=1${localeParam(locale)}`,
    "authors"
  ).then((docs) => docs[0] || null)
}

// ── Blog posts ─────────────────────────────────────────────────────────

/**
 * Full blog-post document — the shape `getBlogPostBySlug` returns. Body fields
 * (`content`, `sources`) are required-but-nullable, NOT optional, so
 * `BlogPostListItem` (which omits them) is genuinely incompatible at the type
 * level. Returning a list-fetch result from a function declared as
 * `Promise<BlogPost | null>` errors at compile time. See `BlogPostListItem`
 * for the full rationale.
 */
export interface BlogPost {
  id: string
  title: string
  slug: string
  excerpt: string
  author: string | CmsAuthor | number | null
  reviewer?: CmsAuthor | number | null
  category: string
  publishedDate: string
  featuredImage?: { url: string; alt?: string } | number | null
  content: LexicalContent | null
  sources: LexicalContent | null
  /**
   * FAQs the editor attached in the CMS via `blog-posts.relatedFaqs`.
   * Populated when the API call uses `depth>=1` (which `getBlogPostBySlug`
   * already does).
   */
  relatedFaqs?: FaqItem[] | null
  /**
   * Markets where the editor has hidden this article (CMS `hideInCountries`).
   * Empty / absent = visible everywhere. Filtered via `isHiddenInCountry`.
   */
  hideInCountries?: Country[]
  /**
   * CMS "SEO & Settings" overrides (flat `seoTitle`/`seoDescription` from
   * `fields/seo.ts`). When set, they take precedence over `title`/`excerpt`
   * for the page `<title>` and `<meta name="description">`.
   */
  seoTitle?: string | null
  seoDescription?: string | null
  /** Payload-managed timestamp; surfaced as `dateModified` in Article JSON-LD. */
  updatedAt?: string
  /**
   * Per-locale old-slug history captured by the CMS's `slugHistoryHook`
   * (AHD-10886). Consumed by `findLocalizedBlogPostSlug`
   * (lib/cms-localized-slugs.ts) to 308-redirect a renamed post's old URL to
   * its current slug.
   */
  previousSlugs?: Record<string, string[]>
  _status?: "draft" | "published"
}

/**
 * Listing/sidebar projection of {@link BlogPost} — what `BLOG_POST_LIST_SELECT`
 * in `lib/cms-server.ts` actually returns. Detail-only body fields (`content`,
 * `sources`) are excluded; because they're declared required (not optional)
 * on `BlogPost`, this exclusion is structurally meaningful — TypeScript
 * rejects passing a `BlogPostListItem` where a `BlogPost` is expected.
 *
 * Detail pages must call `getBlogPostBySlug`, which does a separate
 * full-document fetch.
 */
export type BlogPostListItem = Omit<BlogPost, "content" | "sources">

export interface LexicalContent {
  root: {
    type: string
    children: LexicalNode[]
    [key: string]: unknown
  }
}

export interface LexicalNode {
  type: string
  text?: string
  format?: number
  tag?: string
  url?: string
  children?: LexicalNode[]
  fields?: { linkType?: string; url?: string }
  [key: string]: unknown
}

// `sanitizeSlug` lives in `lib/sanitize-pathname.ts` so the edge proxy
// can use the same sanitizer without pulling in this module's broader
// surface (Sentry, Payload types, etc.). Centralising the implementation
// is what lets the proxy 308-redirect non-canonical URLs to the
// build-time slug form, sidestepping the Next.js `x-next-cache-tags`
// header bug (Sentry MARKETING-SITE-14).
import { HARD_LIST_LIMITS } from "./cms-fetch-limits"
import { sanitizeSlug } from "./sanitize-pathname"
export { sanitizeSlug }

import type { Country } from "@/i18n/config"
import { MESSAGE_FILES, getMessageFile } from "@/i18n/config"
import { isHiddenInCountry } from "./country-gates"

export async function getBlogPosts(
  locale?: string,
  country?: string,
  opts?: { fallbackLocale?: string }
): Promise<BlogPost[]> {
  const fallback =
    opts?.fallbackLocale !== undefined
      ? `&fallback-locale=${opts.fallbackLocale}`
      : ""
  const posts = await cmsGet<BlogPost>(
    `/blog-posts?sort=-publishedDate&limit=50&depth=2${localeParam(locale)}${fallback}`,
    "blog-posts"
  )
  const valid = filterValidBlogPosts("blog-posts", posts).map((p) => ({
    ...p,
    slug: sanitizeSlug(p.slug),
  }))
  if (!country) return valid
  return valid.filter((p) => !isHiddenInCountry(p, country))
}

/**
 * AHD-6013 — drop blog-post docs missing slug or title before sanitization.
 *
 * Mirrors the `filterValidDocs` helper in `lib/cms-collection-index.ts` but
 * lives here so the client-safe `lib/cms.ts` doesn't have to import the
 * server-only collection-index module (which uses `node:fs`).
 */
function filterValidBlogPosts<T extends { id?: number | string }>(
  collection: string,
  docs: T[]
): T[] {
  const valid: T[] = []
  for (const doc of docs) {
    const slug = (doc as { slug?: unknown }).slug
    const title = (doc as { title?: unknown }).title
    const slugOk = typeof slug === "string" && slug.trim() !== ""
    const titleOk = typeof title === "string" && title.trim() !== ""
    if (slugOk && titleOk) {
      valid.push(doc)
      continue
    }
    const missing = [!slugOk && "slug", !titleOk && "title"].filter(
      Boolean
    ) as string[]
    console.warn(
      `[cms] skipping ${collection} doc id=${doc.id ?? "?"} — missing required field(s): ${missing.join(", ")}`
    )
  }
  return valid
}

export async function getBlogPostBySlug(
  slug: string,
  locale?: string
): Promise<BlogPost | null> {
  const exact = await cmsGet<BlogPost>(
    `/blog-posts?where[slug][equals]=${encodeURIComponent(slug)}&limit=1&depth=2${localeParam(locale)}`,
    "blog-posts"
  )
  if (exact[0]) return { ...exact[0], slug: sanitizeSlug(exact[0].slug) }
  const all = await getBlogPosts(locale)
  return all.find((p) => p.slug === slug) || null
}

export async function getPreviewBlogPosts(
  locale?: string,
  opts?: { fallbackLocale?: string }
): Promise<BlogPost[]> {
  const fallback =
    opts?.fallbackLocale !== undefined
      ? `&fallback-locale=${opts.fallbackLocale}`
      : ""
  const posts = await cmsGetWithStatus<BlogPost>(
    `/blog-posts?sort=-publishedDate&limit=50&depth=2&draft=true${localeParam(locale)}${fallback}`,
    "blog-posts-preview",
    { cache: "no-store", headers: previewAuthHeaders() }
  )
  return filterValidBlogPosts("blog-posts-preview", posts.docs).map((p) => ({
    ...p,
    slug: sanitizeSlug(p.slug),
  }))
}

export async function getPreviewBlogPostBySlug(
  slug: string,
  locale?: string
): Promise<BlogPost | null> {
  const exact = await cmsGetWithStatus<BlogPost>(
    `/blog-posts?where[slug][equals]=${encodeURIComponent(slug)}&limit=1&depth=2&draft=true${localeParam(locale)}`,
    "blog-posts-preview",
    { cache: "no-store", headers: previewAuthHeaders() }
  )
  if (exact.docs[0]) {
    return { ...exact.docs[0], slug: sanitizeSlug(exact.docs[0].slug) }
  }

  const all = await getPreviewBlogPosts(locale)
  return all.find((p) => p.slug === slug) || null
}

export async function getPreviewLocations(
  locale?: string
): Promise<CmsLocation[]> {
  const docs = await cmsPreviewGet<CmsLocation>(
    `/locations?sort=clinic&depth=2&limit=100&draft=true${previewLocaleParam(locale)}`,
    "locations-preview"
  )
  return filterPopulatedCityRef(docs, "cms preview")
}

export async function getPreviewAuthors(locale?: string): Promise<CmsAuthor[]> {
  return cmsPreviewGet<CmsAuthor>(
    `/authors?limit=50&draft=true${previewLocaleParam(locale)}`,
    "authors-preview"
  )
}

export async function getPreviewAuthorBySlug(
  slug: string,
  locale?: string
): Promise<CmsAuthor | null> {
  const exact = await cmsPreviewGet<CmsAuthor>(
    `/authors?where[slug][equals]=${encodeURIComponent(slug)}&limit=1&draft=true${previewLocaleParam(locale)}`,
    "authors-preview"
  )
  return exact[0] || null
}

export async function getPreviewFaqItems(locale?: string): Promise<FaqItem[]> {
  // limit=500, not the `faq` registry's 200: that registry is derived from the
  // PUBLISHED count (~176 live), but this `draft=true` fetch also sees
  // never-published drafts, which have already pushed the preview document
  // count past 200 — truncating the editor's preview list and tripping the
  // completeness guard (the DRAFTS blind spot in `cms-fetch-limits.ts`). The
  // higher ceiling returns the full draft set so editors see every item.
  return cmsPreviewGet<FaqItem>(
    `/faq?sort=order&limit=500&draft=true${previewLocaleParam(locale)}`,
    "faq-preview"
  )
}

export async function getPreviewFaqItemBySlug(
  slug: string,
  locale?: string
): Promise<FaqItem | null> {
  const exact = await cmsPreviewGet<FaqItem>(
    `/faq?where[slug][equals]=${encodeURIComponent(slug)}&limit=1&draft=true${previewLocaleParam(locale)}`,
    "faq-preview"
  )
  return exact[0] || null
}

export async function getPreviewFaqItemsByPage(
  page: string,
  locale?: string
): Promise<FaqItem[]> {
  return cmsPreviewGet<FaqItem>(
    `/faq?where[pages][contains]=${encodeURIComponent(page)}&sort=order&limit=8&draft=true${previewLocaleParam(locale)}`,
    "faq-preview"
  )
}

export async function getPreviewJobListings(
  locale?: string
): Promise<JobListing[]> {
  return cmsPreviewGet<JobListing>(
    `/job-listings?sort=order&limit=50&draft=true${previewLocaleParam(locale)}`,
    "jobs-preview"
  )
}

export async function getPreviewJobBySlug(
  slug: string,
  locale?: string
): Promise<JobListing | null> {
  const all = await getPreviewJobListings(locale)
  return (
    all.find((job) => job.slug === slug) ??
    all.find((job) => jobSlug(job) === slug) ??
    null
  )
}

/**
 * Draft biomarker list. Projected with {@link BIOMARKER_LIST_SELECT} because
 * an editor never reads a detail-only field off a list row: the preview detail
 * page fetches its document through `getPreviewCMSBiomarkerBySlug`, which
 * stays unprojected on purpose.
 */
export async function getPreviewCMSBiomarkers(
  locale?: string
): Promise<CMSBiomarkerListItem[]> {
  return cmsPreviewGet<CMSBiomarkerListItem>(
    `/biomarkers?limit=300&sort=name&draft=true${BIOMARKER_LIST_SELECT}${previewLocaleParam(locale)}`,
    "biomarkers-preview"
  )
}

export async function getPreviewCMSBiomarkerBySlug(
  slug: string,
  locale?: string
): Promise<CMSBiomarker | null> {
  const exact = await cmsPreviewGet<CMSBiomarker>(
    `/biomarkers?where[slug][equals]=${encodeURIComponent(slug)}&limit=1&draft=true${previewLocaleParam(locale)}`,
    "biomarkers-preview"
  )
  return exact[0] || null
}

/**
 * Draft siblings for the "related biomarkers" row on the preview biomarker
 * page. The caller reads `slug`, `name` and `description` only, all inside
 * {@link BIOMARKER_LIST_SELECT}. Published-equivalent measurement of the same
 * query (`category=liver`, 15 docs, 2026-07-29): 1.11 MB unprojected vs
 * 0.0104 MB projected.
 */
export async function getPreviewCMSBiomarkersByCategory(
  category: string,
  locale?: string
): Promise<CMSBiomarkerListItem[]> {
  return cmsPreviewGet<CMSBiomarkerListItem>(
    `/biomarkers?where[category][equals]=${encodeURIComponent(category)}&limit=300&sort=name&draft=true${BIOMARKER_LIST_SELECT}${previewLocaleParam(locale)}`,
    "biomarkers-preview"
  )
}

export async function getPreviewCMSDiseases(
  locale?: string
): Promise<CMSDisease[]> {
  return cmsPreviewGet<CMSDisease>(
    `/diseases?limit=750&sort=name&draft=true${previewLocaleParam(locale)}${previewFallbackSuppressParam(locale)}`,
    "diseases-preview"
  )
}

// ── Biomarkers ──────────────────────────────────────────────────────────

export interface CMSBiomarkerSource {
  title: string
  url: string
  id?: string
}

/**
 * Full biomarker document — the shape `getCMSBiomarkerBySlug` returns from a
 * direct, unfiltered fetch. Detail-only fields (`biomarkerId`, `relatedFaqs`,
 * `sources`) are required-but-nullable, NOT optional,
 * so `CMSBiomarkerListItem` (which omits them) is genuinely incompatible at
 * the type level: returning a list-fetch result from a function declared as
 * `Promise<CMSBiomarker | null>` errors at compile time. See
 * `CMSBiomarkerListItem` for the full rationale (PR #653 / #730).
 *
 * Payload always returns these fields on a non-projected fetch, so making
 * them required matches the wire format of the by-slug response.
 */
/**
 * A row from the CMS `categories` collection — the managed, localized version
 * of the coarse taxonomy previously hardcoded in `lib/categories.ts` +
 * `whatWeDetect.catXxx` keys. `name` / `description` arrive already localized
 * for the requested locale.
 *
 * `icon` (an uploaded SVG) and `color` (a hex) are editor-managed presentation,
 * added in marketing-site-cms 85ec9a0. The site renders `icon` as a single-
 * colour CSS-mask silhouette tinted by `color`; when `icon` is blank it falls
 * back to the built-in design-system organ icon resolved from the slug (see
 * `lib/category-icons.tsx`). When `color` is blank the icon inherits
 * `currentColor`.
 */
export interface CmsCategory {
  id: string
  slug: string
  name: string
  description?: string | null
  order?: number
  icon?: { url: string; alt?: string | null; mimeType?: string | null } | null
  color?: string | null
}

// ─── Symptoms ────────────────────────────────────────────────

export interface CmsSymptomGroup {
  id: string
  slug: string
  name: string
  description?: string | null
  order?: number
  icon?: { url: string; alt?: string | null; mimeType?: string | null } | null
  color?: string | null
}

/**
 * A symptom relationship reference, as it comes back from Payload. Shape
 * varies with `depth`: a bare id at depth 0, the populated (partial) doc at
 * depth ≥1. `null` before the CMS migration runs.
 */
export type CmsSymptomRef =
  | string
  | number
  | { id: string | number; slug?: string; name?: string }
  | null

/**
 * A disease relationship reference on a biomarker (`biomarkers.relatedDiseases`).
 * Bare id at depth 0, the populated (partial) doc at depth ≥1. `null`/absent
 * before the CMS migration + backfill land.
 */
export type CmsDiseaseRef =
  | string
  | number
  | {
      id: string | number
      slug?: string
      name?: string
      description?: string | null
    }
  | null

/**
 * A biomarker reference as it comes back inside the `diseases.relatedBiomarkers`
 * reverse join — always a (partial) populated doc, fields per the `populate`.
 */
export interface CmsRelatedBiomarker {
  id: string | number
  slug?: string
  name?: string
  description?: string | null
  category?: string | null
  hideInCountries?: Country[] | null
}

/**
 * A curated "why this symptom relates to this biomarker/condition" relation.
 * `item` is a Payload polymorphic relationship to `biomarkers` or `diseases`.
 */
export interface CmsKeyRelation {
  why?: string | null
  item?: {
    relationTo: "biomarkers" | "diseases"
    value: string | number | { slug?: string; name?: string }
  } | null
  sources?: { label: string; url: string }[] | null
}

export interface CmsSymptom {
  id: string
  slug: string
  name: string
  description?: string | null
  group?: CmsSymptomRef
  synonyms?: { value: string; id?: string | null }[] | null
  keyRelations?: CmsKeyRelation[] | null
  sources?: { label: string; url: string }[] | null
  relatedArticles?:
    | (string | number | { slug?: string; title?: string })[]
    | null
  redFlag?: boolean | null
  order?: number
  _status?: "draft" | "published"
}

/**
 * Listing projection of {@link CmsSymptom}, i.e. what
 * {@link SYMPTOM_LIST_SELECT} actually returns. `sources` and
 * `relatedArticles` are detail-only: `SYMPTOM_DETAIL_SELECT` in
 * `lib/cms-server.ts` adds them and only `SymptomDetail` renders them.
 *
 * Unlike {@link CMSBiomarkerListItem} this `Omit` is documentation, not a
 * structural guard. Both fields are declared optional on `CmsSymptom`, so a
 * list item still satisfies `CmsSymptom` and the compiler will not catch a
 * list result handed to a detail consumer. Turning them into
 * required-but-nullable fields (the `CMSBiomarker` shape) is what would buy
 * the real guard, and that is a wider change than this projection.
 *
 * `keyRelations[].why` is detail-only too, but it lives inside `keyRelations`
 * and so cannot be removed by a top-level `Omit`.
 */
export type CmsSymptomListItem = Omit<CmsSymptom, "sources" | "relatedArticles">

export interface CMSBiomarker {
  id: string
  name: string
  slug: string
  updatedAt?: string
  /**
   * Per-locale old-slug history captured by the CMS's `slugHistoryHook`
   * (AHD-10886). Detail-only — only `BIOMARKER_DETAIL_SELECT` projects it;
   * consumed by `findLocalizedBiomarkerSlug` (lib/cms-localized-slugs.ts) to
   * 308-redirect a renamed biomarker's old URL to its current slug.
   */
  previousSlugs?: Record<string, string[]>
  description: string
  category: string
  packages: string[]
  /**
   * Markets where the editor has hidden this biomarker (CMS `hideInCountries`).
   * Empty / absent = visible everywhere. Filtered via `isHiddenInCountry`.
   *
   * `packages` is global, so this is the only per-market lever on the
   * catalogue: NL dropped four hemolysis-sensitive markers (AHD-12853) that
   * CH and DE still run. A biomarker hidden in a market must not appear in
   * that market's explorer, counts, detail routes, sitemap, search index or
   * llms.txt — see `visibleInCountry` in `lib/country-gates.ts`.
   */
  hideInCountries?: Country[]
  /** One-sentence summary above the description. Optional — falls back to first sentence of description. */
  summary?: string | null
  /** SEO title override. Optional — falls back to the `metaBiomarkerTitle` template. */
  seoTitle?: string | null
  /** Dedicated SERP meta description (decoupled from `summary`, ~155 chars). Optional — falls back to the derived summary. */
  seoDescription?: string | null
  /**
   * Testing-frequency section. Section renders on the biomarker page
   * only when `testingFrequencyAnswer` is present. The other three
   * fields are individually optional.
   */
  testingFrequencyAnswer?: string | null
  testingFrequencyBaseline?: string | null
  testingFrequencyMonitoring?: string | null
  testingFrequencyNote?: string | null
  /** Alternative names ("SGPT", "ALAT"). Captures synonym searches; renders as "Also known as". */
  aliases?: { value: string; id?: string }[] | null
  /** Canonical Ahead biomarker ID (matches Firestore biomarkerInfo.aheadId). */
  biomarkerId: string | null
  /**
   * FAQs the editor attached in the CMS via `biomarkers.relatedFaqs`.
   * Populated when the API call uses `depth>=1` (Payload's default is 2).
   */
  relatedFaqs: FaqItem[] | null
  sources: CMSBiomarkerSource[] | null
  /**
   * Diseases this biomarker helps detect (relationship → `diseases`), the
   * searchable picker for the "Related conditions" section. Populated at
   * depth ≥1; empty array when the editor hasn't linked any.
   */
  relatedDiseases?: CmsDiseaseRef[] | null
  /** Symptoms tagged to this biomarker (relationship → `symptoms`). Used to
   * augment search aliases on the explorer. Populated via the list SELECT. */
  symptoms?: CmsSymptomRef[] | null
  _status?: "draft" | "published"
}

/**
 * Listing/sidebar projection of {@link CMSBiomarker} — what
 * `BIOMARKER_LIST_SELECT` in `lib/cms-server.ts` actually returns.
 *
 * Detail-only fields (`biomarkerId`, `relatedFaqs`, `sources`) are excluded.
 * Because those fields are declared
 * required (not optional) on `CMSBiomarker`, the `Omit` here is structurally
 * meaningful: TypeScript rejects passing a `CMSBiomarkerListItem` where a
 * `CMSBiomarker` is expected. This is the structural guard against the
 * PR #653 / #730 regression, where the listing index was mistakenly served
 * to the detail page and silently dropped the FAQ, Sources, Related
 * conditions, and reference-range chart sections (the chart depends on
 * `biomarkerId` as the join key into `lib/biomarker-clinical-server.ts`).
 *
 * Detail pages must call `getCMSBiomarkerBySlug`, which does a separate
 * full-document fetch.
 */
export type CMSBiomarkerListItem = Omit<
  CMSBiomarker,
  | "biomarkerId"
  | "relatedFaqs"
  | "sources"
  | "testingFrequencyAnswer"
  | "testingFrequencyBaseline"
  | "testingFrequencyMonitoring"
  | "testingFrequencyNote"
>

/**
 * Field projection for symptom-group LIST fetches. Lives here (not in
 * `lib/cms-server.ts`, which imports it) so the browser path and the cached
 * server path cannot drift, same arrangement as {@link BIOMARKER_LIST_SELECT}.
 *
 * Mirrors {@link CATEGORY_CMS_SELECT}: the uploaded icon comes back as a full
 * object via `select[icon]=true` plus `depth=1` on the URL. Do NOT switch to
 * the `populate[media][...]` form, which returns `icon: null` against the live
 * CMS once a `select` projection is applied.
 *
 * This collection is tiny (8 docs, 2,830 B unprojected vs 1,622 B projected,
 * measured 2026-07-29), so the point here is parity with the server twin
 * rather than bytes. `icon` and `color` have no consumer today (the rail and
 * the chips read the hardcoded table in `lib/symptom-groups.ts`), but they are
 * declared on {@link CmsSymptomGroup} and were already in the server
 * projection, so keeping them costs nothing and keeps the two paths identical.
 */
export const SYMPTOM_GROUP_LIST_SELECT =
  "&select[slug]=true&select[name]=true&select[description]=true&select[order]=true&select[active]=true&select[color]=true&select[icon]=true"

/**
 * Field projection for symptom LIST fetches. Same one-definition-two-paths
 * arrangement as {@link BIOMARKER_LIST_SELECT}.
 *
 * `select[]` on its own buys almost nothing on this collection: what collapses
 * the payload is capping the relationships the projection KEEPS. A symptom
 * document is ~71 KB and ~99% of that is `keyRelations[].item.value` expanded
 * to whole biomarker / disease documents at Payload's default `depth=2` (which
 * re-embed their own `symptoms` arrays). Measured against the live CMS
 * 2026-07-29, `limit=200&locale=de`, 60 docs:
 *
 *   no select, default depth                4,484,256 B
 *   select, only the symptom-groups cap,
 *     default depth                         4,402,047 B   (1.8% smaller)
 *   select, only that cap, depth=1            519,480 B   (88.4% smaller)
 *   this select (every cap), default depth     94,954 B   (97.9% smaller)
 *   this select, depth=1                       94,954 B   (byte-identical)
 *   this select, depth=0                       61,002 B   (98.6% smaller)
 *
 * So a projection added without caps is worth 1.8%, and a `depth=1` pin alone
 * still leaves 0.52 MB: the `populate[...]` clauses below are what do the
 * work, by bounding each populated document to slug + name at any depth. The
 * browser fetch then uses `depth=1` for a correctness reason, not a byte one:
 * at `depth=0` a symptom's `group` arrives as a bare id, which
 * `BiomarkerDiseaseExplorer` can only resolve through the separately fetched
 * groups list, so for as long as that second fetch is in flight (or forever,
 * if it fails) every symptom falls out of the grouped view. The server index
 * keeps `depth=0`, because there both lists are awaited before anything
 * renders. The extra 23 KB buys field-parity with today's response.
 *
 * `select[keyRelations][item]` has to name the array subfield;
 * `select[keyRelations]=true` alone returns only the row `id`. `id` itself is
 * returned regardless of the projection, so it is deliberately not selected.
 *
 * The `populate[...]` clauses cap what each populated relation carries. List
 * consumers read `group.slug` / `group.name`; the detail view, which reuses
 * this constant through `SYMPTOM_DETAIL_SELECT` at `depth=2`, reads
 * `keyRelations[].item.value.slug` and `.name` and nothing else off a related
 * biomarker or condition.
 *
 * IMPORTANT: keep this set in sync with {@link CmsSymptomListItem}, and note
 * that `filterValidDocs("symptoms", …, ["slug", "name"])` on the server path
 * drops every doc if `slug` or `name` ever leaves the projection.
 */
export const SYMPTOM_LIST_SELECT =
  "&select[slug]=true&select[name]=true&select[description]=true&select[group]=true&select[synonyms]=true&select[redFlag]=true&select[order]=true&select[keyRelations][item]=true&populate[symptom-groups][slug]=true&populate[symptom-groups][name]=true&populate[biomarkers][slug]=true&populate[biomarkers][name]=true&populate[diseases][slug]=true&populate[diseases][name]=true"

export function getCMSSymptomGroups(
  locale?: string
): Promise<CmsSymptomGroup[]> {
  return cmsGet<CmsSymptomGroup>(
    `/symptom-groups?where[_status][equals]=published&where[active][equals]=true&limit=50&sort=order&depth=1${SYMPTOM_GROUP_LIST_SELECT}${localeParam(locale)}${fallbackLocaleSuppressParam(locale)}`,
    "symptom-groups"
  )
}

export function getCMSSymptoms(locale?: string): Promise<CmsSymptomListItem[]> {
  return cmsGet<CmsSymptomListItem>(
    `/symptoms?where[_status][equals]=published&where[active][equals]=true&limit=200&sort=name&depth=1${SYMPTOM_LIST_SELECT}${localeParam(locale)}${fallbackLocaleSuppressParam(locale)}`,
    "symptoms"
  )
}

export function getCMSSymptomBySlug(
  slug: string,
  locale?: string
): Promise<CmsSymptom | null> {
  return cmsGet<CmsSymptom>(
    `/symptoms?where[slug][equals]=${encodeURIComponent(slug)}&limit=1&depth=2${localeParam(locale)}${fallbackLocaleSuppressParam(locale)}`,
    "symptoms"
  ).then((docs) => docs[0] ?? null)
}

/**
 * Field projection for biomarker LIST fetches. Lives here (not in
 * `lib/cms-server.ts`, which imports it) so the browser path and the cached
 * server path cannot drift.
 *
 * Without it the browser downloaded **11.12 MB** of biomarker JSON; with it,
 * 0.11 MB — a 99.0% cut, measured against the live CMS 2026-07-29. Compression
 * hid this completely (~510 KB on the wire), so the real cost was never
 * bandwidth: it was `JSON.parse` of 11 MB on a mobile main thread, on the
 * critical path of `/go/checkup` (TBT 6757 ms, 7846 ms "Unattributable"
 * bootup). See AHD-9214.
 *
 * IMPORTANT: keep this set in sync with {@link CMSBiomarkerListItem}. The type
 * is what enforces "no detail-only field is read off a list result"; dropping
 * a field from the SELECT without removing it from the type (or vice versa) is
 * the exact shape of the PR #653 regression.
 */
export const BIOMARKER_LIST_SELECT =
  "&select[name]=true&select[slug]=true&select[description]=true&select[category]=true&select[packages]=true&select[hideInCountries]=true&select[summary]=true&select[aliases]=true&select[updatedAt]=true&select[symptoms]=true&populate[symptoms][slug]=true&populate[symptoms][name]=true"

/**
 * Field projection for disease LIST fetches. Same rationale as
 * {@link BIOMARKER_LIST_SELECT}: 10.13 MB -> 0.30 MB (97.1% smaller).
 *
 * `select[aliases]` is new here. The `aliases` docblock on {@link CMSDisease}
 * said to add it "once the CMS migration ships" — it has: an unprojected
 * fetch returns the field today. Until now the SERVER path silently had no
 * disease aliases at all, so `lib/search/page-filter.ts` and
 * `lib/search/cross-locale-aliases.ts` were matching on name only for
 * server-rendered disease lists. Costs 6 KB (0.289 -> 0.295 MB).
 *
 * Not included, deliberately: `relatedBiomarkers` (the server composes its own
 * `joins[relatedBiomarkers]` on top of this constant, and no list consumer
 * reads it) and `_status` (the query already filters on it server-side).
 */
export const DISEASE_LIST_SELECT =
  "&select[slug]=true&select[name]=true&select[category]=true&select[description]=true&select[detectionMethod]=true&select[packages]=true&select[updatedAt]=true&select[aliases]=true&select[symptoms]=true&populate[symptoms][slug]=true&populate[symptoms][name]=true" +
  // `previousSlugs` (AHD-10886) — per-locale old-slug history captured by the
  // CMS's `slugHistoryHook`. This index doubles as `diseaseSlugConfig.loadIndex`
  // (lib/cms-server.ts), so `findLocalizedDiseaseSlug` can 308-redirect a
  // renamed disease's old URL to its current slug. `updatedAt` is already in
  // the set above — it breaks ties when two docs' histories both match.
  "&select[previousSlugs]=true"

export function getCMSBiomarkers(
  locale?: string
): Promise<CMSBiomarkerListItem[]> {
  return cmsGetDeduped<CMSBiomarkerListItem>(
    `/biomarkers?where[_status][equals]=published&limit=300&sort=name${BIOMARKER_LIST_SELECT}${localeParam(locale)}`,
    "biomarkers"
  ).then((r) => r.docs)
}

/**
 * Pass the granular category id (e.g. `cardiovascular`, `liver`). Fetch-all +
 * in-memory filter (rather than `where[category][equals]`) so this shares the
 * cached full-list response with `getCMSBiomarkers` instead of issuing a
 * second query.
 *
 * That sharing is why the URL has to carry {@link BIOMARKER_LIST_SELECT}
 * verbatim: `cmsGetDeduped` keys on `${tag}::${path}`, so the moment the two
 * URLs stop being byte-identical the in-flight coalescing stops working and a
 * caller gets 11.12 MB on top of the 0.11 MB the list already downloaded. See
 * AHD-9301.
 */
export function getCMSBiomarkersByCategory(
  category: string,
  locale?: string
): Promise<CMSBiomarkerListItem[]> {
  return cmsGetDeduped<CMSBiomarkerListItem>(
    `/biomarkers?where[_status][equals]=published&limit=300&sort=name${BIOMARKER_LIST_SELECT}${localeParam(locale)}`,
    "biomarkers"
  )
    .then((r) => r.docs)
    .then((docs) => docs.filter((b) => b.category === category))
}

// ── Diseases ────────────────────────────────────────────────────────────

export interface CMSDisease {
  id: string
  name: string
  slug: string
  /**
   * Per-locale old-slug history captured by the CMS's `slugHistoryHook`
   * (AHD-10886). Projected by `DISEASE_LIST_SELECT`; consumed by
   * `findLocalizedDiseaseSlug` (lib/cms-localized-slugs.ts) to 308-redirect a
   * renamed disease's old URL — and, until every locale has a translated slug,
   * to resolve an English slug arriving on a French URL.
   */
  previousSlugs?: Record<string, string[]>
  category: string
  detectionMethod: "mri" | "blood" | "both" | "ct" | "dexa"
  packages: string[]
  // CMS returns `null` for rows without a populated description. Consumers
  // must handle the null case (search falls back to name-only match, detail
  // pages 404 on missing body, etc.).
  description: string | null
  // Localized synonyms used by the explorer search (`createPageSearcher`).
  // Schema lands in marketing-site-cms PR #124 — the field is optional
  // here so the marketing-site type stays valid before the CMS adds the
  // column. Once the CMS migration ships, add `&select[aliases]=true` to
  // DISEASE_LIST_SELECT in lib/cms-server.ts in a follow-up.
  aliases?: { value: string; id?: string | null }[] | null
  /** Symptoms tagged to this disease (relationship → `symptoms`). Used to
   * augment search aliases on the explorer. Populated via the list SELECT. */
  symptoms?: CmsSymptomRef[] | null
  /**
   * Biomarkers that help detect this disease — the reverse `join` computed by
   * the CMS from `biomarkers.relatedDiseases` (no separate field to maintain).
   * Payload returns it as `{ docs }`. Populated via `joins[relatedBiomarkers]`;
   * empty/absent until the CMS migration + backfill (marketing-site-cms #197)
   * land. */
  relatedBiomarkers?: { docs?: CmsRelatedBiomarker[] } | null
  updatedAt?: string
  _status?: "draft" | "published"
}

/**
 * Next.js Metadata fragment that tells search engines "this is a draft —
 * don't index it." Applied in generateMetadata on every detail route whose
 * BySlug helper resolves draft rows (biomarkers, diseases, insights,
 * careers). Drafts stay visible at their live URL for editor preview, but
 * search engines skip them.
 *
 * Returns `undefined` when the doc is null or already published, so
 * `robots: draftRobots(doc)` is a no-op on published pages — Next.js omits
 * the <meta robots> tag entirely, matching prior behaviour.
 */
export function draftRobots(
  doc: { _status?: "draft" | "published" } | null | undefined
): { index: false; follow: false } | undefined {
  return doc?._status === "draft" ? { index: false, follow: false } : undefined
}

/**
 * Per-locale content gate for diseases. A disease's expandable + detail page
 * are live in a locale only when this returns true. Currently keyed on a
 * genuine localized `description` (see `diseasesIndex` — non-localized
 * descriptions are nulled at the data layer). AND-in future required body
 * fields here; every call site reads through this one function.
 */
export function hasLocalizedContent(
  d: Pick<CMSDisease, "description">
): boolean {
  return !!d.description?.trim()
}

export function getCMSDiseases(locale?: string): Promise<CMSDisease[]> {
  return cmsGetDeduped<CMSDisease>(
    `/diseases?where[_status][equals]=published&limit=750&sort=name${DISEASE_LIST_SELECT}${localeParam(locale)}`,
    "diseases"
  ).then((r) => r.docs)
}

// Mirrors the server `getCategories` select in lib/cms-server.ts. `select[icon]`
// + `depth=1` (on the fetch URL) pulls the uploaded icon as a full object
// (url/mimeType/alt); `select[color]` brings the hex. Do NOT switch to
// `populate[icon][...]`: a `select` projection EXCLUDES unselected fields and
// `populate` alone does not reinstate `icon`, so that combo returned
// `icon: null` against the live CMS.
const CATEGORY_CMS_SELECT =
  "&select[slug]=true&select[name]=true&select[description]=true&select[order]=true&select[active]=true&select[color]=true&select[icon]=true"

/** Client-safe categories fetch — used by the explorer to resolve CMS icons. */
export function getCMSCategories(locale?: string): Promise<CmsCategory[]> {
  return cmsGet<CmsCategory>(
    `/categories?where[active][equals]=true&sort=order&limit=100&depth=1${CATEGORY_CMS_SELECT}${localeParam(locale)}`,
    "categories"
  )
}

export function getCMSDiseaseBySlug(
  slug: string,
  locale?: string
): Promise<CMSDisease | null> {
  return cmsGet<CMSDisease>(
    `/diseases?where[slug][equals]=${encodeURIComponent(slug)}&limit=1${localeParam(locale)}`,
    "diseases"
  ).then((docs) => docs[0] ?? null)
}

/**
 * Pass the granular category id (e.g. `liver`, `gut`). Fetch-all + in-memory
 * filter so this reuses the cached full-list response rather than issuing a
 * second `where[category][equals]` query. Carries
 * {@link DISEASE_LIST_SELECT} for the same reason its biomarker twin carries
 * `BIOMARKER_LIST_SELECT`: a differing URL breaks the `cmsGetDeduped` key and
 * costs 10.13 MB instead of sharing 0.30 MB.
 */
export function getCMSDiseasesByCategory(
  category: string,
  locale?: string
): Promise<CMSDisease[]> {
  return cmsGetDeduped<CMSDisease>(
    `/diseases?where[_status][equals]=published&limit=750&sort=name${DISEASE_LIST_SELECT}${localeParam(locale)}`,
    "diseases"
  )
    .then((r) => r.docs)
    .then((docs) => docs.filter((d) => d.category === category))
}

// ============================================================================
// Testimonials
// ============================================================================

/**
 * Testimonial collection — see marketing-site-cms specs/2026-05-14.
 * Three variants discriminated by `type`. Localized fields (quote,
 * authorTitle, fullVideo.url) come back already resolved to the
 * requested locale by Payload's `?locale=…` parameter.
 */
export type TestimonialType = "text" | "video" | "instagram-video"

/**
 * Which surface a testimonial is allowed to appear on. A single doc can opt
 * into both — a strong review shown in the page carousels AND on the /reviews
 * wall. Carousel queries filter `placements in carousel`; the /reviews page
 * filters `placements in reviews-page`, so reviews-page-only items never leak
 * into a carousel and vice versa.
 */
export type TestimonialPlacement = "carousel" | "reviews-page"

export interface TestimonialMedia {
  id: string
  url: string
  alt?: string
}

export interface Testimonial {
  id: string
  type: TestimonialType
  internalLabel: string
  authorName: string
  authorTitle?: string
  /** Reviewer's gender (CMS-labeled, AHD-8411). Absent = unknown — such
   *  reviews are never excluded by the `audience` query filter. */
  authorGender?: "female" | "male"
  avatar?: TestimonialMedia | null
  quote?: string
  /** Short bold lead-in shown as the headline on the /reviews wall card.
   *  Falls back to the full `quote` when absent. Carousels ignore it. */
  headline?: string
  /** Locale the review was originally written in (e.g. `en`, `de`). Drives
   *  the "translated from" badge on the /reviews wall when it differs from
   *  the viewing locale. */
  originalLocale?: string
  rating?: number
  source: "google" | "trustpilot" | "direct"
  externalUrl?: string
  date?: string
  previewVideo?: TestimonialMedia | null
  fullVideo?: TestimonialMedia | null
  posterImage?: TestimonialMedia | null
  instagramHandle?: string
  instagramFollowers?: string
  instagramUrl?: string
  services?: Array<{ id: string; slug: string; name?: string }>
  tags?: Array<{ id: string; slug: string }>
  /** Surfaces this testimonial may appear on (carousel and/or reviews-page). */
  placements: TestimonialPlacement[]
  availableLocales: string[]
  pinnedToPages?: Array<{ path: string }>
  excludeFromPages?: Array<{ path: string }>
  displayOrder: number
  featured: boolean
}

export interface TestimonialQueryOpts {
  locale?: string
  /** Service relationship IDs. Marketing-site usually doesn't have these
   * at hand — prefer `serviceSlugs` which avoids a slug→ID lookup. */
  /** Raw `products` relation ids. No production caller passes these today —
   *  every surface targets by test slug via `serviceSlugs` below. */
  serviceIds?: string[]
  /** TEST slugs (e.g. `full-body-mri`) — the `/tests/<slug>` page the
   *  testimonial belongs to. Resolved server-side via Payload's
   *  relationship-field-traversal filter on `products.path`, matching
   *  `/tests/<slug>`. The join key is the PATH, not the product slug: a
   *  product's own slug differs from its test slug (`core` owns
   *  `/tests/full-body-mri`), while every caller here passes the test slug. */
  serviceSlugs?: string[]
  tagSlugs?: string[]
  /** Surface filter — the testimonial must be enabled for at least one of
   *  these placements. Applied as an AND clause, so a `["carousel"]` query
   *  never returns reviews-page-only docs (and vice versa). Counts as a
   *  targeting filter for the empty-query guard. */
  placements?: TestimonialPlacement[]
  /** Testimonial-type filter (AND clause). Lets a surface request only some
   *  media kinds — e.g. /ambassadors wants `["video","instagram-video"]`
   *  (reels only, no text), and the per-test cycling bar wants `["text"]`.
   *  A refinement, not a targeting filter — it does NOT satisfy the
   *  empty-query guard on its own. */
  types?: TestimonialType[]
  pagePath?: string
  /** Who the requesting page is for (gendered product pages, AHD-8411).
   *  Excludes testimonials labeled with the *opposite* `authorGender`;
   *  unlabeled testimonials always pass. A refinement, not a targeting
   *  filter — it does NOT satisfy the empty-query guard on its own. */
  audience?: "female" | "male"
  limit?: number
}

/**
 * Build the Payload `where` query string for testimonials.
 *
 * Exported for unit testing — the actual fetch happens in
 * `getCMSTestimonials`. Reads like:
 *
 *   ?where[and][0][_status][equals]=published
 *   &where[and][1][availableLocales][in]=<locale>
 *   &where[and][2][or][0][services.id][in]=<id>,<id>
 *   &where[and][2][or][1][tags.slug][in]=<slug>,<slug>
 *   &where[and][2][or][2][pinnedToPages.path][equals]=<path>
 *   &where[and][3][excludeFromPages.path][not_equals]=<path>
 *   &where[and][4][or][0][authorGender][not_equals]=<opposite>
 *   &where[and][4][or][1][authorGender][exists]=false
 *   &sort=-featured,displayOrder
 *   &limit=<n>
 *   &locale=<locale>
 *
 * Each disjunct in the OR group is skipped when empty.
 */
export function buildTestimonialsQuery(opts: TestimonialQueryOpts): string {
  const {
    locale,
    serviceIds,
    serviceSlugs,
    tagSlugs,
    placements,
    types,
    pagePath,
    audience,
    limit = 12,
  } = opts
  const parts: string[] = []
  let i = 0
  parts.push(`where[and][${i++}][_status][equals]=published`)
  if (locale) {
    parts.push(
      `where[and][${i++}][availableLocales][in]=${encodeURIComponent(locale)}`
    )
  }
  if (placements && placements.length > 0) {
    parts.push(
      `where[and][${i++}][placements][in]=${placements
        .map(encodeURIComponent)
        .join(",")}`
    )
  }
  if (types && types.length > 0) {
    parts.push(
      `where[and][${i++}][type][in]=${types.map(encodeURIComponent).join(",")}`
    )
  }
  // OR group: product match (by relation id OR by test-page path), tag match,
  // or pinned-to-this-page
  const orParts: string[] = []
  let j = 0
  if (serviceIds && serviceIds.length > 0) {
    orParts.push(
      `where[and][${i}][or][${j++}][products][in]=${serviceIds
        .map(encodeURIComponent)
        .join(",")}`
    )
  }
  if (serviceSlugs && serviceSlugs.length > 0) {
    orParts.push(
      `where[and][${i}][or][${j++}][products.path][in]=${serviceSlugs
        .map((slug) => encodeURIComponent(`/tests/${slug}`))
        .join(",")}`
    )
  }
  if (tagSlugs && tagSlugs.length > 0) {
    orParts.push(
      `where[and][${i}][or][${j++}][tags.slug][in]=${tagSlugs
        .map(encodeURIComponent)
        .join(",")}`
    )
  }
  if (pagePath) {
    orParts.push(
      `where[and][${i}][or][${j++}][pinnedToPages.path][equals]=${encodeURIComponent(
        pagePath
      )}`
    )
  }
  if (orParts.length > 0) {
    parts.push(...orParts)
    i++
  }
  if (pagePath) {
    parts.push(
      `where[and][${i++}][excludeFromPages.path][not_equals]=${encodeURIComponent(
        pagePath
      )}`
    )
  }
  if (audience) {
    // Exclude the opposite gender rather than requiring a match, so
    // unlabeled (unknown) testimonials keep showing. The exists=false
    // disjunct is load-bearing: SQL `<> 'male'` is NULL-unsafe, so a plain
    // not_equals would silently drop every unlabeled row.
    const opposite = audience === "female" ? "male" : "female"
    parts.push(
      `where[and][${i}][or][0][authorGender][not_equals]=${opposite}`,
      `where[and][${i}][or][1][authorGender][exists]=false`
    )
    i++
  }
  parts.push(`sort=-featured,displayOrder`)
  parts.push(`limit=${limit}`)
  if (locale) parts.push(`locale=${encodeURIComponent(locale)}`)
  return `/testimonials?${parts.join("&")}`
}

/**
 * Plain fetcher — no cache, safe to call from anywhere. The
 * server-cached wrapper lives in `lib/cms-server.ts`.
 *
 * If no targeting filter (services, tags, pinned page) matches the
 * caller's filters, the OR group is empty and Payload would otherwise
 * return all published rows. We guard that by requiring at least one
 * of the three to be passed.
 */
export function getCMSTestimonials(
  opts: TestimonialQueryOpts
): Promise<Testimonial[]> {
  if (
    (!opts.serviceIds || opts.serviceIds.length === 0) &&
    (!opts.serviceSlugs || opts.serviceSlugs.length === 0) &&
    (!opts.tagSlugs || opts.tagSlugs.length === 0) &&
    (!opts.placements || opts.placements.length === 0) &&
    !opts.pagePath
  ) {
    // No targeting → return empty rather than every published testimonial.
    return Promise.resolve([])
  }
  return cmsGet<Testimonial>(buildTestimonialsQuery(opts), "testimonials")
}
